import { api } from './api'
import { orulTokenManager } from './orul-token-manager'
import { PersistentTokenStorage } from './orul-token-storage'

export type OrgResumo = { key?: string; name?: string } & Record<string, unknown>

type Opcoes = {
  refreshSession: () => Promise<void>
  /** Falso depois que a renovação já foi usada uma vez nesta sessão. */
  permitirRenovacao: boolean
}

export type ResultadoBuscaOrgs<T> = {
  orgs: T[]
  renovou: boolean
}

const ROTA_ORGS = 'orgs/all'

/**
 * Busca as organizações do usuário, renovando a sessão uma vez se a lista vier vazia.
 *
 * O backend resolve as organizações pelo `phone_number` do ID token
 * (`orgs.controller.ts`). Depois de uma troca de telefone, os outros dispositivos seguem
 * com o token antigo: o SDK só renova na montagem SE ele já tiver vencido
 * (`provider.tsx`), então uma aba reaberta antes da expiração consulta pelo número velho,
 * não casa com `clients.phone` — já atualizado pelo phone-sync — e mostra a conta vazia,
 * sem explicar nada ao usuário.
 *
 * Lista vazia também é um estado legítimo (usuário que nunca pontuou), por isso a
 * renovação acontece no máximo uma vez e o resultado dela é aceito como final.
 */
export async function buscarOrgs<T = OrgResumo>({
  refreshSession,
  permitirRenovacao,
}: Opcoes): Promise<ResultadoBuscaOrgs<T>> {
  const { data } = await api.get<T[]>(ROTA_ORGS)

  if (data.length > 0 || !permitirRenovacao) {
    return { orgs: data, renovou: false }
  }

  try {
    await refreshSession()

    // O `orulTokenManager` só é alimentado por um efeito do provider, e a continuação
    // deste await é microtask — o efeito ainda não rodou. Sem empurrar o token novo aqui,
    // a nova tentativa sairia com o mesmo token antigo e repetiria o vazio.
    const renovados = new PersistentTokenStorage().getTokens()

    if (renovados?.idToken) {
      orulTokenManager.setToken(renovados.idToken, renovados.expiresAt)
    }

    const retry = await api.get<T[]>(ROTA_ORGS)

    return { orgs: retry.data, renovou: true }
  } catch {
    // Sem sessão nova segue com a lista vazia: a tela de empresas já sabe lidar com ela.
    return { orgs: data, renovou: true }
  }
}
